import { useEffect } from "react";
import { motion } from "framer-motion";
import { useScroll, useSpring } from "framer-motion";

const PATH = "M50 30C50 13 26 13 26 30C26 47 50 47 50 30C50 13 74 13 74 30C74 47 50 47 50 30Z";
const BRAND_LETTERS = Array.from("VARDHAMAAN", (character, position) => ({
  id: `brand-letter-${position}-${character}`,
  character,
  delay: 0.35 + position * 0.07,
}));

export const InfinitySpinner = ({ size = 130, stroke = "#D4AF37", track = "rgba(255,255,255,0.12)" }) => (
  <svg width={size} height={size * 0.6} viewBox="0 0 100 60" fill="none" data-testid="infinity-spinner">
    <path d={PATH} stroke={track} strokeWidth="4" strokeLinecap="round" />
    <motion.path
      d={PATH}
      stroke={stroke}
      strokeWidth="4"
      strokeLinecap="round"
      initial={{ pathLength: 0.28, pathOffset: 0 }}
      animate={{ pathOffset: [0, 1] }}
      transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
    />
  </svg>
);

export const InitialLoader = ({ onDone }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      document.body.style.overflow = "";
      onDone();
    }, 2500);
    return () => { document.body.style.overflow = ""; clearTimeout(t); };
  }, [onDone]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-[#3A0D2A] flex flex-col items-center justify-center gap-7"
      exit={{ y: "-100%" }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      data-testid="initial-loader"
    >
      <div className="absolute inset-0 hero-pattern opacity-40" />
      <motion.img
        src="/assets/logo-mark.png"
        alt="Vardhamaan logo"
        className="h-24 w-auto relative drop-shadow-[0_4px_16px_rgba(0,0,0,0.4)]"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      />
      <InfinitySpinner size={100} />
      <div className="flex relative">
        {BRAND_LETTERS.map(({ id, character, delay }) => (
          <motion.span
            key={id}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading font-extrabold text-white text-2xl sm:text-3xl tracking-[0.3em]"
          >
            {character}
          </motion.span>
        ))}
      </div>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.6 }} className="font-mono text-[10px] tracking-[0.45em] uppercase text-[#D4AF37] relative">
        Consultancy Services
      </motion.p>
      <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.3, duration: 2, ease: "easeInOut" }} className="h-[2px] w-48 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent origin-center relative" />
    </motion.div>
  );
};

export const RouteLoader = () => (
  <motion.div
    className="fixed inset-0 z-[90] bg-[#3A0D2A]/70 backdrop-blur-md flex items-center justify-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.25 }}
    data-testid="route-loader"
  >
    <InfinitySpinner size={90} />
  </motion.div>
);

export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28, restDelta: 0.001 });
  return <motion.div style={{ scaleX }} className="fixed top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#D4AF37] via-[#F0D77B] to-[#D4AF37] origin-left z-[80]" data-testid="scroll-progress-bar" />;
};
